import { useCallback, useEffect, useMemo, useState } from 'react';
import { CloudTalkActivityLogModal } from '../../components/dem/CloudTalkActivityLogModal';
import { CloudTalkCallDetailModal } from '../../components/dem/CloudTalkCallDetailModal';
import { CloudTalkCallRow } from '../../components/dem/CloudTalkCallRow';
import { CloudTalkContactsModal } from '../../components/dem/CloudTalkContactsModal';
import { CloudTalkFilterRow } from '../../components/dem/CloudTalkFilterRow';
import { useAuth } from '../../contexts/AuthContext';
import { useCloudTalkRealtime } from '../../hooks/useCloudTalkRealtime';
import {
  getCloudTalkCredentials,
  syncCloudTalkAgentsDirectory,
} from '../../lib/cloudTalkApi';
import {
  agentStatusLabel,
  agentStatusTone,
  buildAgentsFromLogs,
  canPlayCloudTalkRecordings,
  cloudTalkFilterLabel,
  countPendingByGroup,
  fetchCloudTalkAgents,
  fetchCloudTalkLogs,
  fetchMissedCallGroups,
  filterCloudTalkCalls,
  groupCloudTalkAgents,
  isSingaporeCallerNumber,
  markCloudTalkCallResolved,
} from '../../lib/demCloudTalk';
import { findDemLinkedPhoneKeys, phoneMatchKey } from '../../lib/demCloudTalkLinks';
import type {
  DemCloudTalkAgent,
  DemCloudTalkCallGroup,
  DemCloudTalkFilter,
  DemCloudTalkLog,
} from '../../types/dem';

const DEFAULT_FILTER: DemCloudTalkFilter = { type: 'pending_group', name: '' };

export function DemCloudTalkPage() {
  const { profile, portalAccess } = useAuth();
  const resolverName = profile?.display_name?.trim() || profile?.username || 'Web user';
  const canManageContacts = canPlayCloudTalkRecordings(portalAccess);

  const [groups, setGroups] = useState<DemCloudTalkCallGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DemCloudTalkFilter>(DEFAULT_FILTER);
  const [selectedGroup, setSelectedGroup] = useState<DemCloudTalkCallGroup | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailGroup, setDetailGroup] = useState<DemCloudTalkCallGroup | null>(null);
  const [resolveBusy, setResolveBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [contactsOpen, setContactsOpen] = useState(false);

  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState<DemCloudTalkLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logAgents, setLogAgents] = useState<DemCloudTalkAgent[]>([]);
  const [demLinkedKeys, setDemLinkedKeys] = useState<Set<string>>(() => new Set());

  /** Critical path only: missed calls + agents. Do not wait on CloudTalk API. */
  const reloadFromDb = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const [callGroups, dbAgents] = await Promise.all([
        fetchMissedCallGroups(),
        fetchCloudTalkAgents().catch(() => [] as DemCloudTalkAgent[]),
      ]);
      let nextAgents = dbAgents;
      if (nextAgents.length === 0) {
        const activityLogs = await fetchCloudTalkLogs(200).catch(() => [] as DemCloudTalkLog[]);
        nextAgents = buildAgentsFromLogs(activityLogs);
      }
      setGroups(callGroups);
      setLogAgents(nextAgents);
      setSelectedGroup((prev) => {
        if (!prev) return null;
        return callGroups.find((g) => g.groupKey === prev.groupKey) ?? null;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load CloudTalk data.');
      setGroups([]);
      setLogAgents([]);
      setSelectedGroup(null);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  /** Background: CloudTalk agent directory sync (slow). Never block first paint. */
  const syncInBackground = useCallback(async () => {
    const creds = getCloudTalkCredentials();
    if (!creds) return;
    await syncCloudTalkAgentsDirectory(creds).catch((e) => {
      console.warn('CloudTalk agent directory sync failed:', e);
    });
    const dbAgents = await fetchCloudTalkAgents().catch(() => [] as DemCloudTalkAgent[]);
    if (dbAgents.length > 0) setLogAgents(dbAgents);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await reloadFromDb(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load CloudTalk data.');
    } finally {
      setLoading(false);
    }
    void syncInBackground();
  }, [reloadFromDb, syncInBackground]);

  useCloudTalkRealtime({
    onChange: () => {
      void reloadFromDb(false);
    },
  });

  const agents = logAgents;
  const agentGroups = useMemo(() => groupCloudTalkAgents(agents), [agents]);
  const pendingByGroup = useMemo(
    () => countPendingByGroup(groups, agents),
    [groups, agents],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Default / recover filter to first team queue (no global Unresolved / Insights).
  useEffect(() => {
    if (agentGroups.length === 0) return;
    const first = agentGroups[0]!.groupName;
    setFilter((prev) => {
      if (prev.type === 'pending' || prev.type === 'insights' || prev.type === 'all') {
        return { type: 'pending_group', name: first };
      }
      if (prev.type === 'pending_group' && !prev.name.trim()) {
        return { type: 'pending_group', name: first };
      }
      if (prev.type === 'pending_group' || prev.type === 'group') {
        if (!agentGroups.some((section) => section.groupName === prev.name)) {
          return { type: 'pending_group', name: first };
        }
        return prev;
      }
      if (prev.type === 'agent') {
        const stillThere = agentGroups.some(
          (section) =>
            section.groupName === prev.groupName &&
            section.agents.some((agent) => agent.name === prev.name),
        );
        return stillThere ? prev : { type: 'pending_group', name: first };
      }
      return prev;
    });
  }, [agentGroups]);

  const visibleCalls = useMemo(
    () => filterCloudTalkCalls(groups, filter, agents),
    [groups, filter, agents],
  );

  useEffect(() => {
    const numbers = visibleCalls.map((row) => row.callerNumber);
    if (!numbers.length) {
      setDemLinkedKeys(new Set());
      return;
    }
    let cancelled = false;
    void findDemLinkedPhoneKeys(numbers)
      .then((keys) => {
        if (!cancelled) setDemLinkedKeys(keys);
      })
      .catch(() => {
        if (!cancelled) setDemLinkedKeys(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [visibleCalls]);

  const openDetail = (group: DemCloudTalkCallGroup) => {
    setSelectedGroup(group);
    setDetailGroup(group);
    setDetailOpen(true);
  };

  const handleMarkResolved = async () => {
    if (!selectedGroup || selectedGroup.status !== 'Pending') return;
    const ids =
      selectedGroup.callIds?.length > 0
        ? selectedGroup.callIds
        : selectedGroup.callId
          ? [selectedGroup.callId]
          : [];
    const count = ids.length || selectedGroup.callCount || 1;
    const confirmMsg =
      count > 1
        ? `Have you returned this caller? Mark all ${count} pending calls for ${selectedGroup.callerNumber} as resolved?`
        : 'Have you returned this call? Mark as resolved?';
    if (!window.confirm(confirmMsg)) return;

    setResolveBusy(true);
    setStatusMessage(null);
    try {
      const resolved = await markCloudTalkCallResolved(ids, resolverName);
      setStatusMessage(
        resolved > 1
          ? `${resolved} calls marked as resolved.`
          : resolved === 1
            ? 'Call marked as resolved.'
            : 'No pending calls left to resolve.',
      );
      await reloadFromDb(false);
    } catch (e) {
      setStatusMessage(e instanceof Error ? e.message : 'Failed to mark as resolved.');
    } finally {
      setResolveBusy(false);
    }
  };

  const openActivityLogs = async () => {
    setLogsOpen(true);
    setLogsLoading(true);
    setLogsError(null);
    try {
      const rows = await fetchCloudTalkLogs(20);
      setLogs(rows);
    } catch (e) {
      setLogsError(e instanceof Error ? e.message : 'Failed to load activity logs.');
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const filterPendingCount = useMemo(() => {
    if (filter.type === 'pending_group') return pendingByGroup[filter.name] ?? 0;
    if (filter.type === 'pending') {
      return groups.filter((g) => g.status === 'Pending' && isSingaporeCallerNumber(g.callerNumber))
        .length;
    }
    return null;
  }, [filter, pendingByGroup, groups]);

  return (
    <div className="dem-cloudtalk-page dem-cloudtalk-page--floating">
      {statusMessage && (
        <p className="dem-uid-status dem-uid-status--ok" role="status">
          {statusMessage}
        </p>
      )}

      {error && <p className="alert alert-error">{error}</p>}

      <div className="dem-cloudtalk-layout">
        <div className="dem-cloudtalk-sidebar card">
          <div className="dem-cloudtalk-panel__head">
            <h2 className="dem-cloudtalk-panel__title">Agents &amp; queues</h2>
          </div>

          <div className="dem-cloudtalk-filter-list">
            {agentGroups.map((section) => {
              const sectionPending = pendingByGroup[section.groupName] ?? 0;
              return (
                <div key={section.groupName} className="dem-cloudtalk-agent-group">
                  <button
                    type="button"
                    className={`dem-cloudtalk-agent-group__head${
                      filter.type === 'group' && filter.name === section.groupName
                        ? ' dem-cloudtalk-agent-group__head--selected'
                        : ''
                    }`}
                    aria-pressed={filter.type === 'group' && filter.name === section.groupName}
                    onClick={() => setFilter({ type: 'group', name: section.groupName })}
                  >
                    <span className="dem-cloudtalk-agent-group__title">{section.groupName}</span>
                    <span className="dem-cloudtalk-agent-group__count">{section.agents.length}</span>
                  </button>
                  <CloudTalkFilterRow
                    label="Unresolved Calls"
                    status={sectionPending > 0 ? String(sectionPending) : undefined}
                    tone={sectionPending > 0 ? 'danger' : 'default'}
                    selected={
                      filter.type === 'pending_group' && filter.name === section.groupName
                    }
                    onSelect={() =>
                      setFilter({ type: 'pending_group', name: section.groupName })
                    }
                  />
                  {section.agents.map((agent) => (
                    <CloudTalkFilterRow
                      key={`${section.groupName}:${agent.name}`}
                      label={agent.name}
                      status={agentStatusLabel(agent.status)}
                      title={agent.name}
                      statusDot={agentStatusTone(agent.status)}
                      selected={
                        filter.type === 'agent' &&
                        filter.name === agent.name &&
                        filter.groupName === section.groupName
                      }
                      onSelect={() =>
                        setFilter({
                          type: 'agent',
                          name: agent.name,
                          groupName: section.groupName,
                        })
                      }
                    />
                  ))}
                </div>
              );
            })}
          </div>

          <div className="dem-cloudtalk-sidebar__foot">
            {canManageContacts ? (
              <button
                type="button"
                className="btn-secondary btn-sm dem-cloudtalk-logs-btn"
                onClick={() => setContactsOpen(true)}
              >
                Contacts
              </button>
            ) : null}
            <button
              type="button"
              className="btn-secondary btn-sm dem-cloudtalk-logs-btn"
              onClick={() => void openActivityLogs()}
            >
              View activity logs
            </button>
          </div>
        </div>

        <div className="dem-cloudtalk-main card">
          <div className="dem-cloudtalk-panel__head dem-cloudtalk-panel__head--toolbar">
            <h2 className="dem-cloudtalk-panel__title">{cloudTalkFilterLabel(filter)}</h2>
            <div className="dem-cloudtalk-panel__tools">
              <span className="dem-sku-map-count-badge">
                {loading
                  ? 'Loading…'
                  : `${visibleCalls.length.toLocaleString()} record${visibleCalls.length === 1 ? '' : 's'}`}
                {filterPendingCount != null && !loading
                  ? ` · ${filterPendingCount} unresolved`
                  : ''}
              </span>
              <span
                className="dem-cloudtalk-sync-badge dem-cloudtalk-sync-badge--live"
                title="Updates when CloudTalk webhook writes to Supabase"
              >
                <span className="dem-cloudtalk-sync-badge__dot" aria-hidden />
                Live
              </span>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => void load()}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="dem-cloudtalk-table-wrap">
            <div className="dem-cloudtalk-table__head" aria-hidden>
              <span>Time</span>
              <span>Contact</span>
              <span>Caller</span>
              <span>Called line</span>
              <span>Status</span>
              <span>Resolved by</span>
            </div>

            <div className="dem-cloudtalk-table__body">
              {loading && visibleCalls.length === 0 ? (
                <p className="dem-cloudtalk-empty">Loading calls…</p>
              ) : visibleCalls.length === 0 ? (
                <p className="dem-cloudtalk-empty">No calls in this queue.</p>
              ) : (
                visibleCalls.map((group) => (
                  <CloudTalkCallRow
                    key={group.groupKey}
                    group={group}
                    demLinked={demLinkedKeys.has(phoneMatchKey(group.callerNumber))}
                    selected={
                      selectedGroup?.groupKey === group.groupKey ||
                      (detailOpen && detailGroup?.groupKey === group.groupKey)
                    }
                    onSelect={setSelectedGroup}
                    onOpenDetail={openDetail}
                  />
                ))
              )}
            </div>
          </div>

          <div className="dem-cloudtalk-main__foot">
            {(filter.type === 'pending' || filter.type === 'pending_group') &&
            selectedGroup?.status === 'Pending' ? (
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={resolveBusy}
                onClick={() => void handleMarkResolved()}
              >
                {resolveBusy
                  ? 'Saving…'
                  : selectedGroup.callCount > 1
                    ? `Mark ${selectedGroup.callCount} calls as resolved`
                    : 'Mark selected as resolved'}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <CloudTalkCallDetailModal
        group={detailGroup}
        insight={null}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
        }}
      />

      <CloudTalkActivityLogModal
        logs={logs}
        open={logsOpen}
        loading={logsLoading}
        error={logsError}
        onClose={() => setLogsOpen(false)}
      />

      {canManageContacts ? (
        <CloudTalkContactsModal open={contactsOpen} onClose={() => setContactsOpen(false)} />
      ) : null}
    </div>
  );
}
